

// ============================================================
// FILE 4: src/components/TrailBuddy/TrailBuddyChat.jsx
// Chat con polling
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext.tsx';
import * as api from '../../services/trailBuddyApi';
import './TrailBuddy.css';

const POLL_INTERVAL = 12000; // 12 secondi

export default function TrailBuddyChat({ eventId, eventName, onClose }) {
    const { user, getToken } = useAuth();
    const [messages, setMessages] = useState([]);
    const [members, setMembers] = useState([]);
    const [text, setText] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const messagesEndRef = useRef(null);
    const lastMessageTime = useRef(null);
    const pollRef = useRef(null);

    // Scroll in fondo ai messaggi
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Carica messaggi
    const loadMessages = useCallback(async (isInitial = false) => {
        try {
            const token = await getToken();
            const after = isInitial ? null : lastMessageTime.current;
            const data = await api.getChat(token, eventId, after);

            if (isInitial) {
                setMessages(data.messages || []);
                setMembers(data.members || []);
            } else if (data.messages?.length > 0) {
                setMessages(prev => {
                    const existingIds = new Set(prev.map(m => m._id));
                    const newMsgs = data.messages.filter(m => !existingIds.has(m._id));
                    return [...prev, ...newMsgs];
                });
            }

            // Aggiorna timestamp per polling incrementale
            const allMsgs = data.messages || [];
            if (allMsgs.length > 0) {
                lastMessageTime.current = allMsgs[allMsgs.length - 1].sentAt;
            }

            setLoading(false);
        } catch (err) {
            if (isInitial) setError(err.message);
            setLoading(false);
        }
    }, [eventId, getToken]);

    // Caricamento iniziale + polling
    useEffect(() => {
        loadMessages(true);
        pollRef.current = setInterval(() => loadMessages(false), POLL_INTERVAL);
        return () => clearInterval(pollRef.current);
    }, [loadMessages]);

    // Scroll su nuovi messaggi
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Invia messaggio
    const handleSend = async () => {
        if (!text.trim() || sending) return;

        const msgText = text.trim();
        setText('');
        setSending(true);

        try {
            const token = await getToken();
            const result = await api.sendMessage(token, eventId, msgText);

            // Aggiungi subito il messaggio alla lista (ottimistic update)
            setMessages(prev => [...prev, result.message]);
            lastMessageTime.current = result.message.sentAt;
        } catch (err) {
            setError('Errore invio messaggio');
            setText(msgText); // Ripristina il testo
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (loading) {
        return <div className="tb-chat-loading">Caricamento chat...</div>;
    }

    return (
        <div className="tb-chat">
            {/* Header */}
            <div className="tb-chat-header">
                <button className="tb-btn-icon" onClick={onClose}>←</button>
                <div className="tb-chat-header-info">
                    <h3>{eventName}</h3>
                    <span className="tb-chat-members">{members.length} partecipanti</span>
                </div>
            </div>

            {error && <p className="tb-otp-error">{error}</p>}

            {/* Messaggi */}
            <div className="tb-chat-messages">
                {messages.length === 0 && (
                    <div className="tb-chat-empty">
                        <p>👋 Nessun messaggio ancora.</p>
                        <p>Inizia la conversazione!</p>
                    </div>
                )}

                {messages.map((msg) => {
                    const isMine = msg.senderUid === user?.uid;
                    return (
                        <div key={msg._id} className={`tb-chat-bubble ${isMine ? 'tb-mine' : 'tb-other'}`}>
                            {!isMine && (
                                <span className="tb-chat-sender">{msg.senderName}</span>
                            )}
                            <p className="tb-chat-text">{msg.text}</p>
                            <span className="tb-chat-time">
                                {new Date(msg.sentAt).toLocaleTimeString('it-IT', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })}
                            </span>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="tb-chat-input-area">
                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Scrivi un messaggio..."
                    className="tb-chat-input"
                    rows={1}
                    maxLength={500}
                />
                <button
                    className="tb-btn tb-btn-send"
                    onClick={handleSend}
                    disabled={!text.trim() || sending}
                >
                    {sending ? '...' : '➤'}
                </button>
            </div>
        </div>
    );
}